<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import {
    capitalize,
    scheduleBadgeText,
  } from '@/domain/catalog/catalog-status-text';
  import { scheduleSummary } from '@/domain/catalog/schedule-form';
  import { ISimpleHourRange } from '@/interfaces/ISimpleHourRange';

  /**
   * «Horarios» in an editor (plan §5.12): «Todo el día» or one line per
   * window, «Cambiar» (the schedule dialog); the plan's upsell when the
   * store's tier lacks it; read only for roles that cannot change it.
   */
  const props = defineProps<{
    value?: ISimpleHourRange[] | null;
    canEdit: boolean;
    /** The role could, but the plan lacks it: «Disponible en el plan Plus». */
    upsell: boolean;
    /** `/{stringId}` of the plan link. */
    stringId?: string | null;
  }>();

  const emit = defineEmits<{ (e: 'edit'): void }>();

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const lines = computed(() =>
    scheduleSummary(props.value ?? null).map((line) =>
      capitalize(scheduleBadgeText(t, [line]) ?? '')
    )
  );

  const planPath = computed(() =>
    props.stringId ? `/${props.stringId}/settings?tab=plan` : null
  );
</script>

<template>
  <div class="v2-catalog-schedule-summary">
    <div class="schedule-head">
      <span class="schedule-label">{{ t('menuPage.editor.schedule') }}</span>
      <span v-if="upsell" class="schedule-plan">Plus</span>
      <PosUiButton
        v-if="canEdit"
        class="schedule-edit"
        color="neutral"
        variant="ghost"
        @click="emit('edit')"
        >{{ t('menuPage.editor.change') }}</PosUiButton
      >
    </div>
    <p v-if="!lines.length" class="schedule-line">
      {{
        value && value.length
          ? t('menuPage.schedule.noValidHours')
          : t('menuPage.schedule.always')
      }}
    </p>
    <template v-else>
      <p v-for="line in lines" :key="line" class="schedule-line">
        {{ line }}
      </p>
    </template>
    <p v-if="upsell" class="schedule-upsell">
      {{ t('menuPage.upsell.plus') }} ·
      <router-link v-if="planPath" :to="planPath" class="upsell-link">{{
        t('menuPage.upsell.cta')
      }}</router-link>
    </p>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-schedule-summary {
    @apply tw-py-1;

    .schedule-head {
      @apply tw-flex tw-min-h-[44px] tw-items-center;

      .schedule-label {
        @apply tw-min-w-0 tw-flex-1 tw-text-sm tw-font-medium tw-text-neutral-900;
      }

      .schedule-plan {
        @apply tw-mr-2 tw-rounded-full tw-bg-neutral-100 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-bold tw-uppercase tw-leading-none tw-tracking-wide tw-text-neutral-600;
      }

      .schedule-edit {
        @apply tw-flex-none;
      }
    }

    .schedule-line {
      @apply tw-m-0 tw-text-sm tw-text-neutral-600;
    }

    .schedule-upsell {
      @apply tw-m-0 tw-mt-1 tw-text-sm tw-text-neutral-500;

      .upsell-link {
        @apply tw-relative tw-font-semibold tw-text-neutral-900 tw-underline;

          // A 44 px tall hit area around the inline link.
          &::after {
            content: '';
            position: absolute;
            top: -12px;
            bottom: -12px;
            left: -4px;
            right: -4px;
          }
      }
    }
  }
</style>
